import { isGuestCountValue, guestCountText } from "./guest-count-value";
// Human-readable fact values (#532) — structured extraction values never render as
// raw JSON. Pure text helpers, shared by the workspace screens.
import type { TFunc } from "../i18n";

export function isEmptyVal(v: unknown): boolean {
  return v === null || v === undefined || v === "";
}

// Bookkeeping keys (schema_version, evidence texts already shown as quotes) are
// dropped from the display.
const HIDDEN_VALUE_KEYS = /^(?:schema_)?version$|(^|_)evidence_text$/;

/** Key label via the i18n dictionary (factKey.<key>) with a prettified fallback. */
export function factKeyLabel(t: TFunc, key: string): string {
  const pretty = key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  return t(`factKey.${key}`, undefined, pretty);
}

export function valToStr(v: unknown, t: TFunc): string {
  if (isGuestCountValue(v)) return guestCountText(v, t);
  if (isEmptyVal(v)) return t("common.noInfo");
  if (v && typeof v === "object" && !Array.isArray(v)) {
    const value = v as Record<string, unknown>;
    if (value.version === 1 && value.currency === "EUR" && "amount_minor" in value) {
      const amount = (Number(value.amount_minor) / 100).toFixed(2);
      const upper = value.kind === "range" ? `–${(Number(value.upper_minor) / 100).toFixed(2)}` : "";
      return `${t(`workflow.budgetKind.${value.kind}`)} ${amount}${upper} EUR · ${t(`workflow.budgetBasis.${value.basis}`)} · ${t(`workflow.tax.${value.stated_tax_basis}`)}`;
    }
    if (value.version === 1 && value.timezone === "Europe/Berlin" && "start" in value)
      return `${value.date} · ${value.start}–${value.end} · Europe/Berlin`;
    if (value.version === 1 && "need" in value && "supplier" in value && Array.isArray(value.details)) {
      return [t(`ws.input.need.${value.need}`), t(`ws.input.supplier.${value.supplier}`), ...value.details].join(" · ");
    }
    if (value.version === 1 && "name" in value && "kind" in value) {
      return `${value.name} · ${t(`ws.input.kind.${value.kind}`)}`;
    }
    if (value.version === 1 && "street" in value && "postal_code" in value) {
      return [[value.street, value.house_number].filter(Boolean).join(" "),
        [value.postal_code, value.locality].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    }
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return t("ws.input.noItems");
    const parts = v.map((x) => valToStr(x, t)).filter((s) => s !== t("common.noInfo"));
    return parts.length ? parts.join("; ") : t("common.noInfo");
  }
  if (typeof v === "boolean") return v ? t("common.yes") : t("common.no");
  if (typeof v === "object") {
    const entries = Object.entries(v as Record<string, unknown>)
      .filter(([key]) => !HIDDEN_VALUE_KEYS.test(key))
      .filter(([, value]) => !isEmptyVal(value) && !(Array.isArray(value) && value.length === 0));
    if (!entries.length) return t("common.noInfo");
    return entries
      .map(([key, value]) => `${factKeyLabel(t, key)}: ${valToStr(value, t)}`)
      .join(" · ");
  }
  if (typeof v === "string") {
    // Schema enum tokens translate via factValue.<token>; free text passes through.
    return t(`factValue.${v}`, undefined, v);
  }
  return String(v);
}
